/**
 * Format checked rows for the LINE-chat clipboard handoff.
 *
 * Two-level layout — brand header, then one block per model with its sizes
 * listed underneath. The model name is stripped of every "(...)" group
 * (load/speed index, XL, sidewall markings) so every size of the "same"
 * model collapses into one block the supplier can scan quickly:
 *
 *   บริดจสโตน
 *
 *   ECOPIA EP300
 *   195/60-15 = 8
 *   195/65-15 = 8
 *
 *   DURAVIS R611
 *   215/70-15 = 16
 *
 * Brand/model/size fall back to skuParser when DB metadata is missing, so
 * orphan SKUs still render a recognizable label.
 */

import { resolveSkuDisplay } from './skuParser.js';

export interface CopyableRow {
  sku: string;
  brand: string | null;
  model: string | null;
  size: string | null;
  qty: number;
}

const NO_BRAND_LABEL = '(ไม่ระบุยี่ห้อ)';

/**
 * Drop every "(...)" group from a model name — load/speed index like
 * "(88V)", "(XL)", sidewall markings like "(*อักษรดำ)" — so sizes of the
 * same base model group together.
 */
function cleanModel(model: string): string {
  return model
    .replace(/\([^)]*\)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function formatCopyText(rows: CopyableRow[]): string {
  if (rows.length === 0) return '';

  type Enriched = { brand: string; model: string; size: string; qty: number };
  const enriched: Enriched[] = rows.map((r) => {
    const d = resolveSkuDisplay(r.sku, r.brand, r.model, r.size);
    const model = d.model ? cleanModel(d.model) : '';
    return {
      brand: d.brand ?? NO_BRAND_LABEL,
      model: model || r.sku,
      size: d.size ?? r.sku,
      qty: r.qty,
    };
  });

  // brand → model → rows, preserving first-seen order at both levels so the
  // operator's existing scan path (alpha by size / supplier-grouped) holds.
  const brandOrder: string[] = [];
  const brands = new Map<
    string,
    { modelOrder: string[]; models: Map<string, Enriched[]> }
  >();
  for (const r of enriched) {
    let b = brands.get(r.brand);
    if (!b) {
      b = { modelOrder: [], models: new Map() };
      brands.set(r.brand, b);
      brandOrder.push(r.brand);
    }
    let m = b.models.get(r.model);
    if (!m) {
      m = [];
      b.models.set(r.model, m);
      b.modelOrder.push(r.model);
    }
    m.push(r);
  }

  const sections: string[] = [];
  for (const brand of brandOrder) {
    const b = brands.get(brand)!;
    const blocks: string[] = [];
    for (const model of b.modelOrder) {
      const list = b.models.get(model)!;
      const lines = [model];
      for (const r of list) lines.push(`${r.size} = ${r.qty}`);
      blocks.push(lines.join('\n'));
    }
    sections.push(`${brand}\n\n${blocks.join('\n\n')}`);
  }

  return sections.join('\n\n');
}
